// ─── Types & constants extracted from ShipmentDetailPage ─────────────────────

import type { NationalRouteStop } from "@/lib/national-route"

export type ShipmentStatus = "en_planificacion" | "planificado" | "activo" | "en_curso" | "finalizado" | "cancelado"
export type WorkLine       = "nacional" | "internacional" | "batea"

export interface ProductRow {
  id:            string
  productId:     string
  productName:   string | null
  productCode:   string | null
  cargoTypeName: string | null
  quantity:      string | null
  weightKg:      string | null
  notes:         string | null
}

export interface ShipmentDetail {
  id:                            string
  internalId:                    number
  internationalIds:              string[]
  status:                        ShipmentStatus
  workLine:                      WorkLine | null
  externalId:                    string | null
  cargoValue:                    string | null
  notes:                         string | null
  billingNotes:                  string | null
  scheduledOriginArrivalAt:      string | null
  scheduledDestinationArrivalAt: string | null
  deliveryNoteStatus:            "normal" | "validado" | "reclamado"
  deletedAt:                     string | null
  createdAt:                     string
  updatedAt:                     string
  clientId:                      string | null
  clientName:                    string | null
  cargoTypeId:                   string | null
  cargoTypeName:                 string | null
  routeId:                       string | null
  routeCode:                     string | null
  routeName:                     string | null
  routeKm:                       number | null
  routeOriginCityName:           string | null
  routeTargetCityId:             string | null
  routeTargetCityName:           string | null
  cargoRouteCode:                string | null
  cargoRouteName:                string | null
  shipmentKm:                   number | null
  senderId:                      string | null
  senderName:                    string | null
  senderAddress:                 string | null
  senderCityName:                string | null
  senderCountryName:             string | null
  recipientId:                   string | null
  recipientName:                 string | null
  recipientAddress:              string | null
  recipientCityName:             string | null
  recipientCountryName:          string | null
  deliveryNoteNumber:            string | null
  loadingWeightKg:               string | null
  unloadingWeightKg:             string | null
  originArrivalAt:               string | null
  originLoadingAt:               string | null
  originDepartureAt:             string | null
  customsOriginArrivalAt:        string | null
  customsOriginDepartureAt:      string | null
  borderOriginArrivalAt:         string | null
  borderOriginDepartureAt:       string | null
  borderCrossingAt:              string | null
  borderDestinationArrivalAt:    string | null
  borderDestinationDepartureAt:  string | null
  customsDestinationArrivalAt:   string | null
  customsDestinationDepartureAt: string | null
  destinationArrivalAt:          string | null
  destinationUnloadingAt:        string | null
  finalizedAt:                   string | null
  cancelReason:                  string | null
  cancelDescription:             string | null
  isRouted:                      boolean
  isMultiLeg:                    boolean
  loadingWindow:                 string | null
  finalDestinationId:            string | null
  products:                      ProductRow[]
  billingTags:                   Array<{ id: string; label: string }>
  tripPlan:                      Record<string, unknown> | null
  techCheck:                     Record<string, unknown> | null
}

export type ShipmentDocType = "remito_carga" | "remito_descarga"

export interface ShipmentDocTypeOption {
  id: string
  code: string
  name: string
  control: "T" | "C" | null
}

export interface ShipmentDocRow {
  id:               string
  fileKey:          string
  signedUrl:        string
  mimeType:         string
  fileName:         string | null
  notes:            string | null
  docType:          ShipmentDocType | null
  docTypeId:        string
  docTypeCode:      string
  docTypeName:      string
  docTypeControl:   "T" | "C" | null
  letter:           string | null
  uploaderFullName: string | null
  createdAt:        string
}

export interface ClientOption    { id: string; name: string; code: string }
export interface CargoTypeOption { id: string; name: string; code: string }
export interface PartyOption     { id: string; name: string; externalId: string | null; cityId: string | null; cityName: string | null; countryName: string | null }
export interface RouteOption     {
  id: string
  code: string
  name: string
  km: number | null
  originCityId: string
  originCityName: string
  targetCityId: string
  targetCityName: string
  deletedAt?: string | null
}

export type TripStatus = "pendiente" | "en_curso" | "completado" | "cancelado"

export const TRIP_STATUS_LABELS: Record<TripStatus, string> = {
  pendiente:  "Pendiente",
  en_curso:   "En curso",
  completado: "Completado",
  cancelado:  "Cancelado",
}

export const TRIP_STATUS_COLORS: Record<TripStatus, string> = {
  pendiente:  "bg-muted text-muted-foreground",
  en_curso:   "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  completado: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  cancelado:  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
}

export const ALL_TRIP_STATUSES: TripStatus[] = ["pendiente", "en_curso", "completado", "cancelado"]

export interface TripRow {
  id:                  string
  internalId:          number
  shipmentId:          string
  mobileId:            string | null
  driverId:            string | null
  truckId:             string | null
  trailerId:           string | null
  operatorName:        string | null
  driverName:          string | null
  truckLicensePlate:    string | null
  truckUnitNumber:      number | null
  trailerLicensePlate:  string | null
  trailerUnitNumber:    number | null
  originCityId:         string | null
  originCityName:      string | null
  destinationCityId:   string | null
  destinationCityName: string | null
  departureAt:         string | null
  arrivalAt:           string | null
  routeKm:             number | null
  status:              TripStatus
  billingStatus:       "liquidado" | "facturado" | "cerrado" | null
  notes:               string | null
  hasTravelAdvance:    "viaje" | "gnc" | "viaje_gnc" | null
  deletedAt:           string | null
  createdAt:           string
}

export interface CityOption { id: string; name: string; code: string }

export const EMPTY_TRIP_FORM = {
  mobileId:          "",
  driverId:          "",
  truckId:           "",
  trailerId:         "",
  originCityId:      "",
  destinationCityId: "",
  departureAt:       "",
  arrivalAt:         "",
  status:            "pendiente" as TripStatus,
  notes:             "",
  hasTravelAdvance:  null as "viaje" | "gnc" | "viaje_gnc" | null,
}
export type TripFormState = typeof EMPTY_TRIP_FORM

export const EMPTY_FORM = {
  status:                        "en_planificacion" as ShipmentStatus,
  clientId:                      "",
  cargoTypeId:                   "",
  externalId:                    "",
  cargoValue:                    "",
  workLine:                      "" as WorkLine | "",
  senderId:                      "",
  recipientId:                   "",
  scheduledOriginArrivalAt:      "",
  scheduledDestinationArrivalAt: "",
  notes:                         "",
  billingNotes:                  "",
  deliveryNoteNumber:    "",
  loadingWeightKg:       "",
  unloadingWeightKg:     "",
  deliveryNoteStatus:    "normal" as "normal" | "validado" | "reclamado",
  loadingWindow:       "",
  finalDestinationId: "",
  routeId:            "",
  routeStops:         [] as NationalRouteStop[],
  isRouted:           false,
  isMultiLeg:         false,
  billingTags:        [] as string[],
  internationalIds:   [] as string[],
  confirmInternationalIdsDeletion: false,
  products: [] as Array<{ productId: string; quantity: string; weightKg: string; notes: string }>,
}
export type FormState = typeof EMPTY_FORM

export type ValorizationType = 'cruce' | 'descarga' | 'estadia' | 'flete' | 'demora' | 'gastos_frontera'

export interface ValorizationRow {
  id:         string
  shipmentId: string
  driverId:   string | null
  type:       ValorizationType
  quantity:   string
  value:      string
  fileUrl:    string | null
  fileKey:    string | null
  mimeType:   string | null
  notes:      string | null
  signedUrl:  string | null
  createdAt:  string
  updatedAt:  string
}

export const VALORIZATION_TYPE_LABELS: Record<ValorizationType, string> = {
  cruce:           "Cruce",
  descarga:        "Descarga",
  estadia:         "Estadía",
  flete:           "Flete",
  demora:          "Demora",
  gastos_frontera: "Gastos de frontera",
}

export const VALORIZATION_TYPE_VALUES: ValorizationType[] = [
  'cruce', 'descarga', 'estadia', 'flete', 'demora', 'gastos_frontera',
]

export const EMPTY_VALORIZATION_FORM = {
  type:     'estadia' as ValorizationType,
  quantity: "",
  value:    "",
  notes:    "",
}
export type ValorizationFormState = typeof EMPTY_VALORIZATION_FORM

export const STATUS_LABELS: Record<ShipmentStatus, string> = {
  en_planificacion: "En planificación",
  planificado:      "Planificado",
  activo:           "Activo",
  en_curso:         "En curso",
  finalizado:       "Finalizado",
  cancelado:        "Cancelado",
}

export const STATUS_COLORS: Record<ShipmentStatus, string> = {
  en_planificacion: "bg-muted text-muted-foreground",
  planificado:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  activo:           "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  en_curso:         "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  finalizado:       "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  cancelado:        "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
}

export const CANCEL_REASON_LABELS: Record<string, string> = {
  clima:    "Clima",
  rotura:   "Rotura",
  chofer:   "Chofer",
  externo:  "Externo",
  otro:     "Otro",
}

export const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
}

export const WORK_LINE_COLORS: Record<WorkLine, string> = {
  nacional:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  internacional: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  batea:         "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
}

export const ALL_STATUSES: ShipmentStatus[] = [
  "en_planificacion", "planificado", "activo", "en_curso", "finalizado", "cancelado",
]

// Statuses available in edit forms — "cancelado" is excluded because
// cancellation must go through the dedicated cancel action (requires reason + description).
export const EDITABLE_STATUSES: ShipmentStatus[] = [
  "en_planificacion", "planificado", "activo", "en_curso", "finalizado",
]

export type JourneyMilestone = {
  key: keyof ShipmentDetail
  label: string
  onlyFor?: WorkLine[]
}

export const JOURNEY_MILESTONES: JourneyMilestone[] = [
  { key: "originArrivalAt",               label: "Llegada al origen" },
  { key: "originLoadingAt",               label: "Carga en origen" },
  { key: "originDepartureAt",             label: "Salida de origen" },
  { key: "customsOriginArrivalAt",        label: "Llegada aduana origen",   onlyFor: ["internacional"] },
  { key: "customsOriginDepartureAt",      label: "Salida aduana origen",    onlyFor: ["internacional"] },
  { key: "borderOriginArrivalAt",         label: "Llegada frontera origen", onlyFor: ["internacional"] },
  { key: "borderOriginDepartureAt",       label: "Salida frontera origen",  onlyFor: ["internacional"] },
  { key: "borderCrossingAt",              label: "Cruce de frontera",       onlyFor: ["internacional"] },
  { key: "borderDestinationArrivalAt",    label: "Llegada frontera destino",onlyFor: ["internacional"] },
  { key: "borderDestinationDepartureAt",  label: "Salida frontera destino", onlyFor: ["internacional"] },
  { key: "customsDestinationArrivalAt",   label: "Llegada aduana destino",  onlyFor: ["internacional"] },
  { key: "customsDestinationDepartureAt", label: "Salida aduana destino",   onlyFor: ["internacional"] },
  { key: "destinationArrivalAt",          label: "Llegada al destino" },
  { key: "destinationUnloadingAt",        label: "Descarga en destino" },
  { key: "finalizedAt",                   label: "Finalizado" },
]

export interface TechCheckItem {
  item: string
  status: "ok" | "observado" | null
}

export interface TechCheck {
  truck?:   TechCheckItem[]
  trailer?: TechCheckItem[]
  savedAt?: string
}

export interface TripPlanEquipmentItem {
  item: string
  status: "ok" | "no_ok" | null
}

export interface TripPlan {
  departureDate?: string
  restHours?: number
  equipment?: {
    truck:   TripPlanEquipmentItem[]
    trailer: TripPlanEquipmentItem[]
  }
  route?: {
    id: string
    origen: string
    destino: string
    tipo_movil: string
    paradas:   Record<string, string[]>
    descansos?: Record<string, boolean>
  } | null
  weatherConditions?: Array<{
    day: number
    date: string
    ratings: Record<string, string>
  }>
  savedAt?: string
}

export const WEATHER_CONDITION_META: Record<string, { label: string; emoji: string }> = {
  soleado: { label: "Soleado",      emoji: "☀️"  },
  viento:  { label: "Viento +40km", emoji: "💨"  },
  lluvia:  { label: "Lluvia",       emoji: "🌧️"  },
  granizo: { label: "Granizo",      emoji: "🌨️"  },
  nieve:   { label: "Nieve",        emoji: "❄️"  },
  helada:  { label: "Helada",       emoji: "🥶"  },
}

export const WEATHER_RATING_META: Record<string, { label: string; className: string; badgeClassName: string }> = {
  good:    { label: "Bueno",   className: "bg-green-50 border-green-200",  badgeClassName: "bg-green-100 text-green-800" },
  regular: { label: "Regular", className: "bg-gray-50 border-gray-200",   badgeClassName: "bg-gray-100 text-gray-600"  },
  bad:     { label: "Malo",    className: "bg-red-50 border-red-200",     badgeClassName: "bg-red-100 text-red-700"    },
}

export const WEATHER_CONDITION_ORDER = ["soleado", "viento", "lluvia", "granizo", "nieve", "helada"]
